// Download event — tracks PDF and file downloads (spec section 4.2.1)

import type { SyntheticEvent } from 'react';
import { pushAdobeEvent, type ClickType } from './core';
import { getCurrentPagePayload } from './pageLoad';
import { trackAdobeClick } from './click';

export type DownloadParams = {
  clickName: string;
  documentName: string;
  clickType?: ClickType;
  clickLocation?: string;
};

// Derives the document name from a download URL (last path segment, decoded)
export const getDocumentNameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://x')
      .pathname;
    const last = pathname.split('/').filter(Boolean).pop() ?? '';
    return decodeURIComponent(last);
  } catch {
    return '';
  }
};

// Tracks a Download — documentName is required (use getDocumentNameFromUrl when only the URL is known)
// A download link is also a click, so a Click event is fired first, immediately followed by the Download event
export const trackAdobeDownload = (
  params: DownloadParams,
  source?: SyntheticEvent<Element> | Element | null
): void => {
  // Fire the Click event first; reuses click auto-derivation (location/type/url) from the source element
  trackAdobeClick(
    {
      clickName: params.clickName,
      clickType: params.clickType,
      clickLocation: params.clickLocation,
    },
    source
  );

  const eventName = 'Download';
  const page = getCurrentPagePayload();
  pushAdobeEvent({
    event: eventName,
    eventName: eventName,
    eventDesc: `${eventName} - ${params.clickName}`,
    ...(page && { page }),
    download: {
      documentName: params.documentName,
    },
  });
};
